import JSZip from 'jszip';
import * as XLSX from 'xlsx';
import type { StockClassification, XkStock } from '@/lib/api/types';
import type { NewXotski } from '@/features/stock/AddXotskiDialog';

export type ConflictDecision = 'replace' | 'createNew';

export interface ParsedRow {
  rowIndex: number;
  payload: NewXotski;
  imageFilename?: string;
  logoFilename?: string;
  errors: string[];
  duplicateOf: XkStock | null;
  decision: ConflictDecision;
}

export interface ParsedImport {
  rows: ParsedRow[];
  imageBlobs: Map<string, Blob>;
  fatalError: string | null;
  totalErrors: number;
}

const VALID_CLASS: ReadonlySet<StockClassification> = new Set([
  'SEE',
  'ENGAGE',
  'TRY',
  'ACT',
]);

const MAX_ZIP_BYTES = 50 * 1024 * 1024;

function normalizeName(s: unknown): string {
  return String(s ?? '').trim().toLowerCase();
}

function isHttpUrl(s: string): boolean {
  return /^https?:\/\//i.test(s);
}

function pickHeader(headers: string[], wanted: string): string | undefined {
  const target = wanted.toLowerCase();
  return headers.find((h) => h.trim().toLowerCase() === target);
}

export async function parseImportZip(
  file: File,
  existingStock: XkStock[],
): Promise<ParsedImport> {
  const result: ParsedImport = {
    rows: [],
    imageBlobs: new Map(),
    fatalError: null,
    totalErrors: 0,
  };

  if (!/\.zip$/i.test(file.name)) {
    result.fatalError = 'notAZip';
    return result;
  }
  if (file.size > MAX_ZIP_BYTES) {
    result.fatalError = 'zipTooLarge';
    return result;
  }

  let zip: JSZip;
  try {
    zip = await JSZip.loadAsync(file);
  } catch {
    result.fatalError = 'parseFailed';
    return result;
  }

  const xlsxEntries: JSZip.JSZipObject[] = [];
  zip.forEach((path, entry) => {
    if (entry.dir) return;
    if (/\.xlsx$/i.test(path)) xlsxEntries.push(entry);
  });

  if (xlsxEntries.length === 0) {
    result.fatalError = 'noXlsx';
    return result;
  }
  if (xlsxEntries.length > 1) {
    result.fatalError = 'multipleXlsx';
    return result;
  }

  const imagePaths = new Map<string, JSZip.JSZipObject>();
  zip.forEach((path, entry) => {
    if (entry.dir) return;
    if (/\.xlsx$/i.test(path)) return;
    if (/(^|\/)(README|readme)\.txt$/.test(path)) return;
    const base = path.split('/').pop();
    if (!base) return;
    imagePaths.set(base.toLowerCase(), entry);
  });

  const xlsxBuf = await xlsxEntries[0].async('arraybuffer');
  let wb: XLSX.WorkBook;
  try {
    wb = XLSX.read(xlsxBuf, { type: 'array', cellDates: true });
  } catch {
    result.fatalError = 'parseFailed';
    return result;
  }

  const sheet = wb.Sheets[wb.SheetNames[0]];
  const json = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, {
    defval: null,
    raw: true,
  });

  if (json.length === 0) {
    result.fatalError = 'emptySheet';
    return result;
  }

  const headers = Object.keys(json[0]);
  const nameCol = pickHeader(headers, 'Name');
  if (!nameCol) {
    result.fatalError = 'missingNameCol';
    return result;
  }

  const translateCol = pickHeader(headers, 'Translate');
  const descriptionCol = pickHeader(headers, 'Description');
  const imageCol = pickHeader(headers, 'Image');
  const logoCol = pickHeader(headers, 'Logo');
  const ratingCol = pickHeader(headers, 'Rating');
  const classCol = pickHeader(headers, 'Classification');
  const initQtyCol = pickHeader(headers, 'Initial qty');
  // Unit cost UI is hidden — we no longer read it from the spreadsheet.
  // Keep the column name comment here as a hint for when we re-enable.
  const restockCol = pickHeader(headers, 'Restock threshold');

  const existingByName = new Map<string, XkStock>();
  for (const s of existingStock) {
    if (s.name) existingByName.set(normalizeName(s.name), s);
  }

  const referencedFilenames = new Set<string>();

  for (let i = 0; i < json.length; i++) {
    const raw = json[i];
    const errors: string[] = [];

    const name = String(raw[nameCol] ?? '').trim();
    if (!name) {
      errors.push('nameRequired');
    } else if (name.length < 2) {
      errors.push('nameTooShort');
    }

    let rating: number | undefined;
    if (ratingCol && raw[ratingCol] != null && raw[ratingCol] !== '') {
      const r = Number(raw[ratingCol]);
      if (!Number.isFinite(r) || r < 1 || r > 5) {
        errors.push('ratingRange');
      } else {
        rating = Math.round(r);
      }
    }

    // Classification UI is hidden — every imported row gets the 'SEE'
    // default, regardless of what the ZIP contains. We still tolerate a
    // valid value in the spreadsheet for forward compat, but an unknown
    // value no longer fails the row.
    let classification: StockClassification = 'SEE';
    if (classCol && raw[classCol] != null && raw[classCol] !== '') {
      const c = String(raw[classCol]).trim().toUpperCase();
      if (VALID_CLASS.has(c as StockClassification)) {
        classification = c as StockClassification;
      }
    }

    let initialQuantity: number | undefined;
    if (initQtyCol && raw[initQtyCol] != null && raw[initQtyCol] !== '') {
      const q = Number(raw[initQtyCol]);
      if (!Number.isFinite(q) || !Number.isInteger(q)) {
        errors.push('qtyNotInt');
      } else {
        initialQuantity = q;
      }
    }

    // Unit cost intentionally not read — column is ignored even if
    // present in the spreadsheet. Rows are imported with `unitCost`
    // undefined so the DB column stays null.

    let restockThreshold: number | undefined;
    if (restockCol && raw[restockCol] != null && raw[restockCol] !== '') {
      const r = Number(raw[restockCol]);
      if (Number.isFinite(r) && r >= 0 && Number.isInteger(r)) {
        restockThreshold = r;
      }
    }

    let imageValue: string | undefined;
    let imageFilename: string | undefined;
    if (imageCol && raw[imageCol] != null && raw[imageCol] !== '') {
      const v = String(raw[imageCol]).trim();
      if (v) {
        if (isHttpUrl(v)) {
          imageValue = v;
        } else {
          imageFilename = v;
          const key = v.toLowerCase();
          referencedFilenames.add(key);
          if (!imagePaths.has(key) && !imagePaths.has(key.split('/').pop()!)) {
            errors.push(`imageNotFound:${v}`);
          }
        }
      }
    }

    let logoValue: string | undefined;
    let logoFilename: string | undefined;
    if (logoCol && raw[logoCol] != null && raw[logoCol] !== '') {
      const v = String(raw[logoCol]).trim();
      if (v) {
        if (isHttpUrl(v)) {
          logoValue = v;
        } else {
          logoFilename = v;
          const key = v.toLowerCase();
          referencedFilenames.add(key);
          if (!imagePaths.has(key) && !imagePaths.has(key.split('/').pop()!)) {
            errors.push(`logoNotFound:${v}`);
          }
        }
      }
    }

    const translate =
      translateCol && raw[translateCol] != null
        ? String(raw[translateCol]).trim() || undefined
        : undefined;
    const description =
      descriptionCol && raw[descriptionCol] != null
        ? String(raw[descriptionCol]).trim() || undefined
        : undefined;

    const duplicateOf = name ? existingByName.get(normalizeName(name)) ?? null : null;

    const payload: NewXotski = {
      name,
      translate,
      description,
      image: imageValue,
      logo: logoValue,
      rating: rating ?? 3,
      classification,
      initialQuantity,
      // unitCost omitted — see "Unit cost intentionally not read" above.
      restockThreshold,
    };

    result.rows.push({
      rowIndex: i + 2, // +2: 1-based + header row
      payload,
      imageFilename,
      logoFilename,
      errors,
      duplicateOf,
      decision: 'replace',
    });
    result.totalErrors += errors.length;
  }

  for (const filename of referencedFilenames) {
    const entry = imagePaths.get(filename) ?? imagePaths.get(filename.split('/').pop()!);
    if (entry) {
      const blob = await entry.async('blob');
      result.imageBlobs.set(filename, blob);
    }
  }

  return result;
}

export function rowIsImportable(row: ParsedRow): boolean {
  return row.errors.length === 0;
}

export function summarizeParse(parsed: ParsedImport): {
  importable: number;
  duplicates: number;
  errors: number;
} {
  let importable = 0;
  let duplicates = 0;
  for (const r of parsed.rows) {
    if (rowIsImportable(r)) importable++;
    if (r.duplicateOf) duplicates++;
  }
  return { importable, duplicates, errors: parsed.totalErrors };
}
